import React, { useState, useEffect, useCallback } from 'react';
import { callTasksApi, CallTask, CallTaskStats } from '../api/callTasks';
import CallTaskModal from '../components/CallTaskModal';
import { emailMatches } from '../utils/emailSearch';
import './CallTasks.css';

export const CallTasks: React.FC = () => {
  const [tasks, setTasks] = useState<CallTask[]>([]);
  const [stats, setStats] = useState<CallTaskStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<CallTask | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState<'pending' | 'completed'>('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 50;

  const showMessage = (type: 'success' | 'error' | 'info', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const loadTasks = useCallback(async () => {
    try {
      const offset = (currentPage - 1) * pageSize;
      const response = await callTasksApi.getTasks({
        status: filter,
        limit: pageSize,
        offset: offset
      });
      setTasks(response.tasks || []);

      // Calculate total count based on whether we got a full page
      // If we get less than pageSize tasks, we're on the last page
      const returnedCount = response.tasks?.length || 0;
      if (returnedCount < pageSize) {
        // We're on the last page or got all results
        setTotalCount(offset + returnedCount);
      } else if (currentPage === 1) {
        // On first page with full results, estimate we have at least 2 pages
        setTotalCount(pageSize * 2);
      }
    } catch (error: any) {
      console.error('Error loading tasks:', error);
      showMessage('error', error.response?.data?.error || 'Failed to load tasks');
    }
  }, [filter, currentPage, pageSize]);

  const loadStats = useCallback(async () => {
    try {
      const response = await callTasksApi.getStats();
      setStats(response.stats);
    } catch (error: any) {
      console.error('Error loading stats:', error);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadTasks(), loadStats()]);
    setLoading(false);
  }, [loadTasks, loadStats]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleViewTask = (task: CallTask) => {
    console.log('🔍 handleViewTask called with task:', task);
    setSelectedTask(task);
    setShowModal(true);
    console.log('🔍 showModal set to true');
  };

  const handleTaskCompleted = () => {
    setShowModal(false);
    setSelectedTask(null);
    loadAll();
    showMessage('success', 'Task completed successfully!');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTask(null);
  };

  const filteredTasks = tasks.filter(task => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      task.clientName?.toLowerCase().includes(search) ||
      // gmail.com and googlemail.com are the same mailbox: treat as equivalent.
      emailMatches(task.clientEmail, searchTerm) ||
      task.phoneNumber?.includes(search)
    );
  });

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  if (loading) {
    return (
      <div className="call-tasks-container">
        <div className="loading-state">Loading call tasks...</div>
      </div>
    );
  }

  return (
    <div className="call-tasks-container">
      <div className="call-tasks-header">
        <div className="header-title">
          <h1>Call Recording Tasks</h1>
          <p>Review call transcriptions and assign to claims</p>
        </div>

        {stats && (
          <div className="stats-cards">
            <div className="stat-card">
              <div className="stat-value">{stats.pendingTasks}</div>
              <div className="stat-label">Pending</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.completedToday}</div>
              <div className="stat-label">Completed Today</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.totalTasks}</div>
              <div className="stat-label">Total Tasks</div>
            </div>
          </div>
        )}
      </div>

      {message && (
        <div className={`message-banner message-${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="tasks-controls">
        <div className="filter-tabs">
          <button
            className={`filter-tab ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => {
              setFilter('pending');
              setCurrentPage(1);
            }}
          >
            Pending ({stats?.pendingTasks || 0})
          </button>
          <button
            className={`filter-tab ${filter === 'completed' ? 'active' : ''}`}
            onClick={() => {
              setFilter('completed');
              setCurrentPage(1);
            }}
          >
            Completed ({stats?.completedTasks || 0})
          </button>
        </div>

        <div className="search-box">
          <input
            type="text"
            placeholder="Search by client name, email, or phone..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      <div className="tasks-table-container">
        <table className="tasks-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Phone</th>
              <th>User</th>
              <th>Call Date</th>
              <th>Duration</th>
              <th>Created</th>
              {filter === 'completed' && <th>Completed</th>}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTasks.length === 0 ? (
              <tr>
                <td colSpan={filter === 'completed' ? 8 : 7} className="no-tasks">
                  No {filter} tasks found
                </td>
              </tr>
            ) : (
              filteredTasks.map((task) => (
                <tr key={task.id}>
                  <td>
                    <div className="client-info">
                      <div className="client-name">{task.clientName || 'Unknown'}</div>
                      <div className="client-email">{task.clientEmail}</div>
                    </div>
                  </td>
                  <td>{task.phoneNumber}</td>
                  <td>
                    {task.userFirstname && task.userLastname
                      ? `${task.userFirstname} ${task.userLastname}`
                      : 'Unknown'}
                    {task.userExtension && <div className="extension">Ext: {task.userExtension}</div>}
                  </td>
                  <td>{formatDate(task.callDatetime)}</td>
                  <td>{formatDuration(task.durationSeconds)}</td>
                  <td>{formatDate(task.createdAt)}</td>
                  {filter === 'completed' && <td>{formatDate(task.completedAt)}</td>}
                  <td>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleViewTask(task)}
                    >
                      {filter === 'pending' ? 'Review' : 'View'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {filteredTasks.length > 0 && (
        <div className="pagination-controls" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb'
        }}>
          <div style={{ color: '#6b7280', fontSize: '14px' }}>
            Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, ((currentPage - 1) * pageSize) + filteredTasks.length)} of {totalCount || filteredTasks.length} tasks
          </div>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              className="btn btn-secondary"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                backgroundColor: currentPage === 1 ? '#f3f4f6' : '#ffffff',
                color: currentPage === 1 ? '#9ca3af' : '#374151',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Previous
            </button>

            <span style={{
              padding: '8px 12px',
              backgroundColor: '#ffffff',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151'
            }}>
              Page {currentPage}
            </span>

            <button
              className="btn btn-secondary"
              onClick={() => setCurrentPage(prev => prev + 1)}
              disabled={filteredTasks.length < pageSize}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                backgroundColor: filteredTasks.length < pageSize ? '#f3f4f6' : '#ffffff',
                color: filteredTasks.length < pageSize ? '#9ca3af' : '#374151',
                cursor: filteredTasks.length < pageSize ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showModal && selectedTask && (
        <CallTaskModal
          task={selectedTask}
          onComplete={handleTaskCompleted}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};
